import { InfoOutlined, UploadFile } from "@mui/icons-material";
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Collapse,
    IconButton,
    LinearProgress,
    TextField,
    Typography,
} from "@mui/material";
import { useState } from "react";
import * as XLSX from "xlsx";
import { ReadingService } from "../../../Services/ReadingService";

export const DeleteReadingWithExcel = () => {
    const [deleteFile, setDeleteFile] = useState<File | null>(null);
    const [deleteLoading, setDeleteLoading] = useState<
        "loading" | "initial" | "finished"
    >("initial");
    const [deleteSuccesses, setDeleteSuccesses] = useState<any>([]);
    const [deleteFailures, setDeleteFailures] = useState<any>([]);
    const [showDeleteResponseDetails, setShowDeleteResponseDetails] =
        useState(false);
    const [deleteMonth, setDeleteMonth] = useState("2");
    const [deleteYear, setDeleteYear] = useState("2026");

    const handleDeleteFileUpload = async () => {
        const reader = new FileReader();

        if (deleteFile) {
            setDeleteLoading("loading");

            reader.onload = async (evt) => {
                const binary = evt.target?.result;
                const wb = XLSX.read(binary, { type: "binary" });
                const sheets = wb.Sheets;
                let deleteRows: any[] = [];

                for (const sheet of Object.values(sheets)) {
                    const json = XLSX.utils.sheet_to_json(sheet, {
                        raw: false,
                        dateNF: "yyyy-mm-dd",
                    });
                    deleteRows = deleteRows.concat(json);
                }

                const [data, err] = await ReadingService.deleteReadingsByExcel(
                    deleteRows,
                    {
                        month: Number(deleteMonth),
                        year: Number(deleteYear),
                    }
                );

                setDeleteFailures(err?.failures || err?.errors || []);
                setDeleteSuccesses(data?.successes || []);
                setDeleteLoading("finished");
            };

            reader.readAsBinaryString(deleteFile);
        }
    };

    return (
        <Card>
            <CardContent>
                <Typography variant="h5" fontWeight="bold">
                    Delete Reading With Excel
                </Typography>

                <Typography variant="body2" color="text.secondary" mt={1}>
                    Upload an Excel file containing Meter No values. The backend
                    will resolve the matching reading for the selected
                    month/year and delete it.
                </Typography>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns:
                            "repeat(auto-fit, minmax(180px, 1fr))",
                        gap: 2,
                        mt: 2,
                        mb: 2,
                    }}
                >
                    <TextField
                        label="Delete Month"
                        select
                        SelectProps={{ native: true }}
                        value={deleteMonth}
                        onChange={(e) => setDeleteMonth(e.target.value)}
                    >
                        {Array.from({ length: 12 }, (_, index) => (
                            <option key={index + 1} value={index + 1}>
                                {new Date(0, index).toLocaleString("en", {
                                    month: "long",
                                })}
                            </option>
                        ))}
                    </TextField>
                    <TextField
                        label="Delete Year"
                        select
                        SelectProps={{ native: true }}
                        value={deleteYear}
                        onChange={(e) => setDeleteYear(e.target.value)}
                    >
                        {Array.from({ length: 20 }, (_, index) => {
                            const y = new Date().getFullYear() - 10 + index;
                            return (
                                <option key={y} value={y}>
                                    {y}
                                </option>
                            );
                        })}
                    </TextField>
                </Box>

                <Box display="flex" gap={1} mt={2}>
                    <Button
                        variant="contained"
                        color="error"
                        disabled={deleteLoading === "loading"}
                        startIcon={
                            deleteLoading === "loading" ? (
                                <CircularProgress size={20} />
                            ) : (
                                <UploadFile />
                            )
                        }
                        onClick={() => {
                            deleteFile && handleDeleteFileUpload();
                        }}
                    >
                        Delete Readings
                    </Button>
                </Box>

                <input
                    onChange={(e) => {
                        setDeleteFile(
                            e.target.files?.length ? e.target.files[0] : null
                        );
                    }}
                    type="file"
                    accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    style={{ marginTop: 12 }}
                />

                {deleteLoading === "loading" && (
                    <LinearProgress sx={{ mt: 2 }} />
                )}

                <section hidden={deleteLoading !== "finished"}>
                    <Alert
                        severity="warning"
                        sx={{ mt: 2 }}
                        action={
                            <IconButton
                                onClick={() => {
                                    setShowDeleteResponseDetails(
                                        !showDeleteResponseDetails
                                    );
                                }}
                            >
                                <InfoOutlined fontSize="small" />
                            </IconButton>
                        }
                    >
                        <AlertTitle>
                            {`${deleteSuccesses.length} successes out of ${
                                (deleteSuccesses.length ?? 0) +
                                (deleteFailures.length ?? 0)
                            } `}
                        </AlertTitle>

                        <Collapse in={showDeleteResponseDetails}>
                            {deleteSuccesses?.map(
                                (
                                    suc: {
                                        message: string;
                                        meterNo: number | string;
                                    },
                                    index: number
                                ) => (
                                    <Box key={index}>
                                        {`Meter No - (${suc.meterNo}) - ${suc.message}`}
                                    </Box>
                                )
                            )}

                            {deleteFailures.map(
                                (
                                    err: {
                                        message: string;
                                        meterNo: number | string;
                                    },
                                    index: number
                                ) => (
                                    <Box key={index}>
                                        {`Meter No - (${err.meterNo}) - ${err.message}`}
                                    </Box>
                                )
                            )}
                        </Collapse>
                    </Alert>
                </section>
            </CardContent>
        </Card>
    );
};

export default DeleteReadingWithExcel;
